import {
    BaseRecord,
    ForgotPasswordFormTypes,
    ForgotPasswordPageProps,
    HttpError,
    useForgotPassword,
    useRouterContext,
    useTranslate,
} from '@pankod/refine-core';
import { useForm, UseFormProps } from '@pankod/refine-react-hook-form';
import {
    Alert,
    AlertDescription,
    AlertIcon,
    Box,
    BoxProps,
    Button,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Heading,
    Input,
    Link as ChakraLink,
} from '@chakra-ui/react';

type FormPropsType<TFormType> = UseFormProps & {
    onSubmit?: (values: TFormType) => void;
};

type ForgotPasswordProps = ForgotPasswordPageProps<
    BoxProps,
    BoxProps,
    FormPropsType<ForgotPasswordFormTypes>
>;

const ForgotPasswordPage = ({
    loginLink,
    wrapperProps,
    contentProps,
    renderContent,
    formProps,
}: ForgotPasswordProps) => {
    const { onSubmit, ...useFormProps } = formProps || {};
    const { mutate, isLoading, isSuccess } = useForgotPassword<ForgotPasswordFormTypes>();
    const translate = useTranslate();
    const { Link } = useRouterContext();
    const {
        handleSubmit,
        register,
        formState: { errors },
    } = useForm<BaseRecord, HttpError, ForgotPasswordFormTypes>({
        ...useFormProps,
    });

    const content = (
        <Box bg="chakra-body-bg" width="400px" padding="20px" borderRadius="10px" {...contentProps}>
            <Heading mb="8" textAlign="center" size="lg">
                {translate('pages.forgotPassword.title', 'Forgot your password?')}
            </Heading>

            {isSuccess && (
                <Alert mb="3" status="success">
                    <AlertIcon />
                    <AlertDescription>
                        If an account exists for that email address, reset instructions have been sent.
                    </AlertDescription>
                </Alert>
            )}

            <form
                onSubmit={handleSubmit((data) => {
                    if (onSubmit) {
                        return onSubmit(data);
                    }

                    return mutate(data);
                })}
            >
                <FormControl mb="3" isInvalid={!!errors?.email}>
                    <FormLabel>
                        {translate('pages.forgotPassword.fields.email', 'Email')}
                    </FormLabel>
                    <Input
                        id="email"
                        type="email"
                        {...register('email', {
                            required: true,
                            pattern: {
                                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                message: translate('pages.login.errors.validEmail', 'Invalid email address'),
                            },
                        })}
                    />
                    <FormErrorMessage>{`${errors.email?.message}`}</FormErrorMessage>
                </FormControl>

                {loginLink ?? (
                    <Box mb="3" display="flex" justifyContent="flex-end">
                        <span>{translate('pages.register.buttons.haveAccount', 'Have an account?')}</span>
                        <ChakraLink color="green" ml="1" as={Link} to="/login">
                            {translate('pages.login.signin', 'Sign in')}
                        </ChakraLink>
                    </Box>
                )}

                <Button
                    mb="3"
                    type="submit"
                    width="full"
                    colorScheme="green"
                    isLoading={isLoading}
                    isDisabled={isLoading}
                >
                    {translate('pages.forgotPassword.buttons.submit', 'Send reset instructions')}
                </Button>
            </form>
        </Box>
    );

    return (
        <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            minHeight="100vh"
            {...wrapperProps}
        >
            {renderContent ? renderContent(content) : content}
        </Box>
    );
};

export default ForgotPasswordPage;